import React, { useEffect, useState } from "react";
import ActionMenu from "../../Components/ActionMenu";
import ActionIconButton from "../../Components/ActionIconButton";
import ActionsCell from "../../Components/ActionsCell";
import { exportToCsv, commonUserColumns } from "../../utils/exportCsv";
import {
  AlertTriangle,
  Baby,
  Calendar,
  CheckCircle,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Download,
  Edit3,
  Eye,
  FileDown,
  Filter,
  GraduationCap,
  Heart,
  Layers,
  LinkIcon,
  Mail,
  Plus,
  RefreshCw,
  Search,
  Square,
  Trash2,
  TrendingUp,
  Upload,
  User2,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { Modal } from "antd";
import { API_BASE_URL } from "../../config";

// Constants
const server_url = API_BASE_URL;
const GRADE_LIST = [
    "BV1", "BV2", "BV3", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"
];
const colors = {
    orange: "#D67C2C",
    blue: "#2173B7",
    green: "#22c55e",
    purple: "#a855f7"
};

// Helper function for conditional class names
function classNames(...args) {
    return args.filter(Boolean).join(" ");
}

// Children listed inline before the row defers to the detail modal.
const MAX_CHILDREN_SHOWN = 2;

function ParentView() {
    // State
    const [parents, setParents] = useState([]);
    const [filteredParents, setFilteredParents] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [errorModalVisible, setErrorModalVisible] = useState(false);

    // Bulk Selection
    const [selectedParents, setSelectedParents] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Add Parent Modal
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [formParent, setFormParent] = useState({
        name: "",
        email: "",
        password: "",
    });
    const [showPassword, setShowPassword] = useState(false);

    // Bulk CSV Modal
    const [csvModalOpen, setCSVModalOpen] = useState(false);
    const [csvPreview, setCSVPreview] = useState([]);
    const [csvFile, setCSVFile] = useState(null);

    // CSV Validation State
    const [csvValidation, setCSVValidation] = useState(null);
    const [validating, setValidating] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [showSkippedModal, setShowSkippedModal] = useState(false);


    // Detail Modal
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedParent, setSelectedParent] = useState(null);

    // Link Children Modal
    const [linkChildrenModalOpen, setLinkChildrenModalOpen] = useState(false);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(false);

    // Delete Confirmation Modal
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({
        open: false,
        type: 'single',
        email: '',
        parents: [],
        step: 1
    });

    // Notification - matching LessonPlanHistory.js
    const [notification, setNotification] = useState(null);

    // Filter state - matching LessonPlanHistory.js style
    const [filters, setFilters] = useState({
        status: "",
        school: "",
        grade: "",
        createdStartDate: "",
        createdEndDate: ""
    });

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Get access token
    const accessToken = localStorage.getItem("accessToken");

    // Fetch parents with retry mechanism
    useEffect(() => {
        const fetchWithRetry = async () => {
            try {
                await fetchParents();
            } catch (error) {
                // If we have retries left and there was an error, try again after a delay
                if (retryCount < 3) {
                    console.log(`Retrying fetch (${retryCount + 1}/3)...`);
                    setTimeout(() => {
                        setRetryCount(prevCount => prevCount + 1);
                    }, 1500); // Retry after 1.5 seconds
                } else {
                    // If we've exhausted retries, show the error modal
                    setErrorModalVisible(true);
                }
            }
        };

        fetchWithRetry();
        // eslint-disable-next-line
    }, [retryCount]);

    // Update filtered parents when parents or search/filters change
    useEffect(() => {
        filterParents();
        // eslint-disable-next-line
    }, [parents, search, filters]);

    // Filter parents based on search and filter criteria
    const filterParents = () => {
        const filtered = parents.filter((p) => {
            // Text search
            const matchesSearch = !search ||
                [p.name, p.email, p.role].filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase());

            // Status filter
            const matchesStatus = !filters.status ||
                p.status === filters.status;

            // School filter
            const matchesSchool = !filters.school ||
                p.school_name === filters.school;

            // Grade filter (for parents with children in specific grades)
            const matchesGrade = !filters.grade ||
                (p.children && p.children.some(child => child.grade === filters.grade));

            // Created At date range filter
            const createdDate = p.created_at ? new Date(p.created_at) : null;
            const matchesCreatedStart = !filters.createdStartDate ||
                (createdDate && createdDate >= new Date(filters.createdStartDate));
            const matchesCreatedEnd = !filters.createdEndDate ||
                (createdDate && createdDate <= new Date(`${filters.createdEndDate}T23:59:59.999`));

            return matchesSearch && matchesStatus && matchesSchool && matchesGrade &&
                matchesCreatedStart && matchesCreatedEnd;
        });

        setFilteredParents(filtered);
    };

    // Fetch parents
    async function fetchParents() {
        setLoading(true);
        setError(null);

        try {
            if (!accessToken) {
                throw new Error("Authentication token not found");
            }

            const resp = await fetch(`${server_url}/admin/get-parents`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!resp.ok) {
                throw new Error(`Failed to fetch parents: ${resp.status} ${resp.statusText}`);
            }

            const data = await resp.json();
            setParents(Array.isArray(data) ? data : []);
            setFilteredParents(Array.isArray(data) ? data : []);
            setError(null);
            setErrorModalVisible(false);
        } catch (err) {
            console.error("Error fetching parents:", err);
            setError(err.message || "Error fetching parents");
            setParents([]);
            throw err; // Re-throw for retry logic
        } finally {
            setLoading(false);
        }
    }

    // Calculate statistics for the dashboard cards
    const totalParents = parents.length;
    const recentParents = parents.filter(p => {
        if (!p.created_at) return false;
        const createdDate = new Date(p.created_at);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return createdDate >= thirtyDaysAgo;
    }).length;

    const activeConnections = parents.filter(p => p.children && p.children.length > 0).length;
    const parentsWithProfiles = parents.filter(p => p.profile_picture_url).length;

    const totalPages = Math.ceil(filteredParents.length / pageSize);
    const pagedParents = filteredParents.slice((page - 1) * pageSize, page * pageSize);

    // StatsCard component - exactly like LessonPlanHistory.js
    const StatsCard = ({ title, value, icon: Icon, color, bgColor, subtitle }) => (
        <div
            className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
            <div className="p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500">{title}</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                    </div>
                    <div className="p-3 rounded-full" style={{ backgroundColor: bgColor }}>
                        <Icon className="w-6 h-6" style={{ color: color }} />
                    </div>
                </div>
            </div>
        </div>
    );

    // Dashboard stats adjusted to use the design pattern of LessonPlanHistory.js
    const dashboardStats = [
        {
            icon: Users,
            title: "Total Parents",
            value: totalParents,
            color: colors.blue,
            bgColor: `${colors.blue}10`
        },
        {
            icon: UserCheck,
            title: "Recent Registrations",
            value: recentParents,
            subtitle: "Last 30 days",
            color: colors.green,
            bgColor: `${colors.green}10`
        },
        {
            icon: Heart,
            title: "Active Connections",
            value: activeConnections,
            subtitle: "Parents with linked children",
            color: colors.purple,
            bgColor: `${colors.purple}10`
        },
        {
            icon: TrendingUp,
            title: "Completed Profiles",
            value: parentsWithProfiles,
            subtitle: `${Math.round((parentsWithProfiles / totalParents) * 100) || 0}% completion`,
            color: colors.orange,
            bgColor: `${colors.orange}10`
        }
    ];

    // Bulk Selection Handlers
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedParents([]);
        } else {
            setSelectedParents(pagedParents.map(p => p.id));
        }
        setSelectAll(!selectAll);
    };

    const handleSelectParent = (parentId) => {
        setSelectedParents(prev => {
            if (prev.includes(parentId)) {
                return prev.filter(id => id !== parentId);
            } else {
                return [...prev, parentId];
            }
        });
    };

    // Handle filter changes - matching LessonPlanHistory.js style
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPage(1); // Reset to first page when filters change
    };

    // Clear all filters - matching LessonPlanHistory.js style
    const clearFilters = () => {
        setFilters({
            status: "",
            school: "",
            grade: "",
            createdStartDate: "",
            createdEndDate: ""
        });
        setSearch("");
        setPage(1);
    };

    // Retry fetching parents
    const handleRetryFetch = () => {
        setRetryCount(0); // Reset retry count
        setErrorModalVisible(false); // Close modal
    };

    // Show notification - matching LessonPlanHistory.js
    // Export the whole filtered set, not just the page on screen.
    function handleExportCsv() {
        const rows = filteredParents;
        if (!rows.length) return showNotification("error", "There is nothing to export.");
        const count = exportToCsv("parents", [
            ...commonUserColumns,
            { header: "readonly_children", value: (p) => (p.children || []).map((c) => c.name) },
            { header: "readonly_children_count", value: (p) => (p.children || []).length },
            { header: "readonly_children_emails", value: (p) => (p.children || []).map((c) => c.email) },
            { header: "readonly_children_grades", value: (p) => (p.children || []).map((c) => [c.grade, c.section].filter(Boolean).join(" ")) },
        ], rows);
        showNotification("success", `Exported ${count} parents to CSV.`);
    }

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    // Create Parent Handler
    async function handleCreateParent(e) {
        e.preventDefault();
        const fd = new FormData();
        fd.append("name", formParent.name);
        fd.append("email", formParent.email);
        fd.append("password", formParent.password);
        fd.append("role", "parent");
        try {
            const resp = await fetch(`${server_url}/admin/create-user`, {
                method: "POST",
                headers: { Authorization: `Bearer ${accessToken}` },
                body: fd,
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || "Failed to create parent");
            setAddModalOpen(false);
            setFormParent({ name: "", email: "", password: "" });
            fetchParents();
            showNotification("success", "Parent account created successfully!");
        } catch (err) {
            showNotification("error", err.message || "Failed to create parent");
        }
    }

    // Email validation helper
    const isValidEmail = (email) => {
        if (!email || typeof email !== 'string') return { valid: false, reason: "Empty email" };
        const trimmed = email.trim();
        if (trimmed.includes(' ')) return { valid: false, reason: "Contains spaces" };
        const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!pattern.test(trimmed)) return { valid: false, reason: "Invalid format" };
        return { valid: true, reason: null };
    };

    // Bulk Upload Handlers
    async function handleCSVChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        setCSVFile(file);
        setCSVValidation(null);
        setUploadResult(null);

        const reader = new FileReader();
        reader.onload = async (evt) => {
            const text = evt.target.result;
            const lines = text.split("\n").filter(line => line.trim());
            if (lines.length < 2) {
                setCSVPreview([]);
                return;
            }

            const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
            const emailIdx = headers.indexOf("email");
            const nameIdx = headers.indexOf("name");

            // Parse and validate rows client-side
            const parsedRows = [];
            const emailSet = new Set();

            for (let i = 1; i < lines.length && i <= 20; i++) {
                const cols = lines[i].split(",").map(c => c.trim());
                const email = emailIdx >= 0 ? cols[emailIdx] || "" : "";
                const name = nameIdx >= 0 ? cols[nameIdx] || "" : "";

                let status = "valid";
                let reason = null;

                const emailCheck = isValidEmail(email);
                if (!emailCheck.valid) {
                    status = "invalid_email";
                    reason = emailCheck.reason;
                } else if (emailSet.has(email.toLowerCase())) {
                    status = "duplicate";
                    reason = "Duplicate in file";
                }
                emailSet.add(email.toLowerCase());

                parsedRows.push({
                    row: i + 1,
                    name,
                    email,
                    status,
                    reason
                });
            }

            setCSVPreview(parsedRows);

            // Validate on server
            if (file) {
                setValidating(true);
                try {
                    const fd = new FormData();
                    fd.append("file", file);
                    fd.append("role", "parent");

                    const resp = await fetch(`${server_url}/admin/bulk-operations/validate-csv`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${accessToken}` },
                        body: fd,
                    });

                    if (resp.ok) {
                        const validation = await resp.json();
                        setCSVValidation(validation);
                        if (validation.preview) {
                            setCSVPreview(validation.preview);
                        }
                    } else {
                        // Show error message to user
                        const errorData = await resp.json().catch(() => ({}));
                        const errorMsg = errorData.detail || "CSV validation failed. Please check your file format.";
                        showNotification("error", errorMsg);
                        setCSVFile(null);
                        setCSVPreview([]);
                        setCSVValidation(null);
                    }
                } catch (err) {
                    console.error("Validation error:", err);
                    showNotification("error", "Failed to validate CSV file.");
                } finally {
                    setValidating(false);
                }

            }
        };
        reader.readAsText(file);
    }

    async function handleBulkUpload(e) {
        e.preventDefault();
        if (!csvFile) return showNotification("error", "No CSV file selected.");
        const fd = new FormData();
        fd.append("role", "parent");
        fd.append("file", csvFile);
        try {
            const resp = await fetch(`${server_url}/admin/create-user`, {
                method: "POST",
                headers: { Authorization: `Bearer ${accessToken}` },
                body: fd,
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || "Bulk upload failed.");

            setUploadResult(data);

            let successMsg = `Created ${data.created_count || 0} parents`;
            if (data.skipped_count > 0) {
                successMsg += `. ${data.skipped_count} records were skipped.`;
            }

            setCSVModalOpen(false);
            setCSVFile(null);
            setCSVPreview([]);
            setCSVValidation(null);
            fetchParents();

            if (data.skipped_count > 0) {
                showNotification("warning", successMsg);
                setShowSkippedModal(true);
            } else {
                showNotification("success", successMsg);
            }
        } catch (err) {
            showNotification("error", err.message || "Bulk upload failed.");
        }
    }


    function downloadCSVTemplate() {
        const csv = "name,email,password\nParent Name,parent@example.com,Secret123";
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "parents_template.csv";
        a.click();
    }

    // Delete confirmation handlers
    const openDeleteConfirmation = (type, email = '', parents = []) => {
        setDeleteConfirmModal({
            open: true,
            type,
            email,
            parents,
            step: 1
        });
    };

    const handleDeleteConfirmation = async () => {
        const { type, email, parents, step } = deleteConfirmModal;

        if (step < 3) {
            setDeleteConfirmModal(prev => ({ ...prev, step: prev.step + 1 }));
            return;
        }

        try {
            if (type === 'single') {
                const resp = await fetch(`${server_url}/admin/delete-user/${email}`, {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.detail || "Failed to delete parent");
                showNotification("success", "Parent account deleted successfully");
            } else if (type === 'bulk') {
                const resp = await fetch(`${server_url}/admin/bulk-delete-parents`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ parent_ids: parents }),
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.detail || "Failed to delete parents");
                setSelectedParents([]);
                setSelectAll(false);
                showNotification("success", `${parents.length} parents deleted successfully`);
            }
            setDeleteConfirmModal({ open: false, type: 'single', email: '', parents: [], step: 1 });
            fetchParents();
        } catch (err) {
            showNotification("error", err.message || "Delete failed");
        }
    };

    // Link Children functionality
    const openLinkChildren = async (parent) => {
        setSelectedParent(parent);
        setLinkChildrenModalOpen(true);
        setSelectedStudents([]);
        setLoadingStudents(true);

        try {
            const resp = await fetch(`${server_url}/admin/get-students`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await resp.json();
            setAvailableStudents(Array.isArray(data) ? data : []);
        } catch (e) {
            setAvailableStudents([]);
            showNotification("error", "Failed to fetch students.");
        } finally {
            setLoadingStudents(false);
        }
    };

    const handleLinkChildren = async (e) => {
        e.preventDefault();
        if (!selectedParent || !selectedStudents.length) return;

        try {
            const resp = await fetch(`${server_url}/admin/parent/${selectedParent.id}/link-children`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ student_ids: selectedStudents }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || "Failed to link children");
            setLinkChildrenModalOpen(false);
            setSelectedStudents([]);
            fetchParents();
            showNotification("success", `${selectedStudents.length} children linked successfully!`);
        } catch (err) {
            showNotification("error", err.message || "Failed to link children");
        }
    };

    // Format date for display - matching LessonPlanHistory.js
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get status badge - similar to LessonPlanHistory.js
    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 inline-block mr-1" />
                        Active
                    </span>
                );
            case 'inactive':
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Edit3 className="w-3 h-3 inline-block mr-1" />
                        Inactive
                    </span>
                );
            default:
                return (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {status || 'Unknown'}
                    </span>
                );
        }
    };

    // UI
    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Notification - exactly like LessonPlanHistory.js */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                                notification.type === 'error' ? 'bg-red-500 text-white' :
                                    'bg-blue-500 text-white'
                                }`} style={{ animation: 'slideInRight 0.5s ease-out' }}>
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                                notification.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
                                    <AlertTriangle className="w-5 h-5" />}
                            <span className="font-[poppins]">{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Header - exactly like LessonPlanHistory.js */}
                    <div className="pt-8 pb-8 text-center relative overflow-hidden">
                        <div className="relative z-10">
                            <div>
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                                    style={{ backgroundColor: '#D67C2C' }}>
                                    <Heart className="w-8 h-8 text-white" />
                                </div>
                                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                                    Parent Management Dashboard
                                </h1>
                                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                    Comprehensive parent management system with child linking and family connection
                                    tracking
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Dashboard Cards - exactly like LessonPlanHistory.js */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        {dashboardStats.map((stat, index) => (
                            <StatsCard
                                key={index}
                                title={stat.title}
                                value={stat.value}
                                icon={stat.icon}
                                color={stat.color}
                                bgColor={stat.bgColor}
                                subtitle={stat.subtitle}
                            />
                        ))}
                    </div>

                    {/* Filter Section - exactly like LessonPlanHistory.js */}
                    <div className="bg-white rounded-2xl shadow-md overflow-visible border border-gray-200 mb-8">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                        <Filter className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter
                                        Parents</h2>
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={() => setAddModalOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                                    >
                                        <Plus className="w-5 h-5" />
                                        Create New Parent
                                    </button>

                                    <ActionMenu
                                        label="Bulk Actions"
                                        icon={<Layers className="w-5 h-5" />}
                                        items={[
                                            {
                                                key: "upload",
                                                label: "Bulk Upload (CSV)",
                                                description: "Create parents from a CSV, or update those that already exist.",
                                                icon: <Upload className="w-4 h-4" />,
                                                tone: "primary",
                                                onClick: () => setCSVModalOpen(true),
                                            },
                                            {
                                                key: "export",
                                                label: "Export CSV",
                                                description: "Download every parent matching the current filters.",
                                                icon: <Download className="w-4 h-4" />,
                                                tone: "success",
                                                onClick: handleExportCsv,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            <form onSubmit={(e) => e.preventDefault()}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <Search className="inline-block w-4 h-4 mr-2" />
                                        Search
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="search"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search by name or email..."
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        />
                                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                                    </div>
                                </div>

                                {/* Status filter intentionally left out: the /admin/get-parents
                                    response (ReturnParent) has no status/is_active/is_temp_password
                                    field on parent records, so there is no real data to filter on. */}

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <GraduationCap className="inline-block w-4 h-4 mr-2" />
                                        Child Grade
                                    </label>
                                    <select
                                        name="grade"
                                        value={filters.grade}
                                        onChange={handleFilterChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                    >
                                        <option value="">All Grades</option>
                                        {GRADE_LIST.map((grade) => (
                                            <option key={grade} value={grade}>Grade {grade}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <Calendar className="inline-block w-4 h-4 mr-2" />
                                        Created From
                                    </label>
                                    <input
                                        type="date"
                                        name="createdStartDate"
                                        value={filters.createdStartDate}
                                        onChange={handleFilterChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        <Calendar className="inline-block w-4 h-4 mr-2" />
                                        Created To
                                    </label>
                                    <input
                                        type="date"
                                        name="createdEndDate"
                                        value={filters.createdEndDate}
                                        onChange={handleFilterChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                    />
                                </div>

                                <div
                                    className="md:col-span-3 lg:col-span-6 flex items-center justify-center space-x-4 mt-4">
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        <Search className="inline-block w-4 h-4 mr-2" />
                                        Search
                                    </button>

                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <RefreshCw className="inline-block w-4 h-4 mr-2" />
                                        Clear Filters
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Bulk Actions Bar */}
                    {selectedParents.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                            <div className="p-6 bg-orange-50 border-b border-orange-100">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                            <Users className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div className="text-orange-700 font-semibold">
                                            {selectedParents.length} parent{selectedParents.length > 1 ? 's' : ''} selected
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            onClick={() => openDeleteConfirmation('bulk', '', selectedParents)}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors shadow-md"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedParents([]);
                                                setSelectAll(false);
                                            }}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors shadow-md"
                                        >
                                            <X className="w-4 h-4" />
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Parents Table Section */}
                    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                    <Heart className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                                    Parents ({filteredParents.length})
                                </h2>
                            </div>
                        </div>

                        {/* Table Content */}
                        {loading ? (
                            <div className="flex justify-center items-center p-12">
                                <div className="text-center">
                                    <div
                                        className="w-8 h-8 text-[#D67C2C] animate-spin rounded-full border-4 border-orange-500 border-t-transparent mx-auto"></div>
                                    <p className="mt-4 text-gray-600 font-medium">Loading parents...</p>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="flex justify-center items-center p-12 text-red-500">
                                <AlertTriangle className="w-6 h-6 mr-2" />
                                <span>{error}</span>
                                <button
                                    onClick={handleRetryFetch}
                                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : filteredParents.length === 0 ? (
                            <div className="flex flex-col justify-center items-center p-12 text-gray-500">
                                <Heart className="w-16 h-16 mb-4 text-gray-300" />
                                <p className="text-lg mb-3">No parents found</p>
                                <p className="text-sm text-gray-400 mb-6">
                                    {search || filters.status || filters.school || filters.grade || filters.createdStartDate || filters.createdEndDate ?
                                        "Try clearing your filters or creating a new parent." :
                                        "Get started by creating your first parent."}
                                </p>
                                <button
                                    onClick={() => setAddModalOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                                >
                                    <Plus className="w-5 h-5" />
                                    Create New Parent
                                </button>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                <button onClick={handleSelectAll}
                                                    className="flex items-center gap-2 hover:text-[#D67C2C] transition-colors">
                                                    {selectAll ? <CheckSquare className="w-5 h-5 text-[#D67C2C]" /> :
                                                        <Square className="w-5 h-5" />}
                                                    Select
                                                </button>
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Parent Name
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Email Address
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Created At
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Children
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {pagedParents.map((p) => (
                                            <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleSelectParent(p.id)}
                                                        className="hover:text-[#D67C2C] transition-colors"
                                                    >
                                                        {selectedParents.includes(p.id) ?
                                                            <CheckSquare className="w-5 h-5 text-[#D67C2C]" /> :
                                                            <Square className="w-5 h-5 text-gray-400" />
                                                        }
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">

                                                        <div
                                                            className="flex-shrink-0 h-30 w-30 rounded-lg flex items-center justify-center">
                                                            {p.profile_picture_url ? (
                                                                <img
                                                                    src={server_url + p.profile_picture_url}
                                                                    alt={p.name}
                                                                    className="h-20 w-20 rounded-lg object-cover"
                                                                />
                                                            ) : (
                                                                <User2 className="h-5 w-5 text-[#D67C2C]" />
                                                            )}
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {p.name || 'Unnamed Parent'}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                ID: {p.id ? String(p.id).substring(0, 8) : "N/A"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-sm text-gray-900 flex items-center">
                                                                <Mail className="w-4 h-4 mr-1 text-gray-400" />
                                                                {p.email || 'No email'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex items-center">
                                                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                                                        {formatDate(p.created_at)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {p.children && p.children.length > 0 ? (
                                                        <div className="space-y-1">
                                                            {p.children.slice(0, MAX_CHILDREN_SHOWN).map((child) => (
                                                                <div key={child.id} className="flex items-center gap-2 text-sm">
                                                                    <Baby className="w-3.5 h-3.5 flex-shrink-0 text-orange-500" />
                                                                    <span className="font-medium text-gray-900">{child.name || "Unnamed"}</span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {[child.grade, child.section].filter(Boolean).join(" · ") || "No grade"}
                                                                        {child.class_count ? ` · ${child.class_count} class${child.class_count === 1 ? "" : "es"}` : ""}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {p.children.length > MAX_CHILDREN_SHOWN && (
                                                                <button
                                                                    onClick={() => { setSelectedParent(p); setDetailModalOpen(true); }}
                                                                    className="text-xs font-medium text-[#D67C2C] hover:underline"
                                                                >
                                                                    +{p.children.length - MAX_CHILDREN_SHOWN} more
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-gray-400">No children linked</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <ActionsCell className="justify-end">
                                                        <ActionIconButton
                                                            icon={Eye}
                                                            tone="view"
                                                            onClick={() => {
                                                                setSelectedParent(p);
                                                                setDetailModalOpen(true);
                                                            }}
                                                            title="View Details"
                                                        />

                                                        <ActionIconButton
                                                            icon={LinkIcon}
                                                            tone="link"
                                                            onClick={() => openLinkChildren(p)}
                                                            title="Link Children"
                                                        />

                                                        <ActionIconButton
                                                            icon={Trash2}
                                                            tone="delete"
                                                            onClick={() => openDeleteConfirmation('single', p.email)}
                                                            title="Delete"
                                                        />
                                                    </ActionsCell>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination - exactly like LessonPlanHistory.js */}
                        {!loading && !error && filteredParents.length > 0 && (
                            <div
                                className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="text-sm text-gray-500">
                                        Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredParents.length)} of {filteredParents.length} parents
                                    </div>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                        className="text-sm border border-gray-300 rounded px-2 py-1 text-gray-600"
                                    >
                                        <option value={10}>10 / page</option>
                                        <option value={20}>20 / page</option>
                                        <option value={50}>50 / page</option>
                                        <option value={100}>100 / page</option>
                                    </select>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setPage(Math.max(page - 1, 1))}
                                        disabled={page === 1}
                                        className={`p-2 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>

                                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                                        // Show pages around current page
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (page <= 3) {
                                            pageNum = i + 1;
                                        } else if (page >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = page - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={i}
                                                onClick={() => setPage(pageNum)}
                                                className={`px-3 py-1 rounded-md ${pageNum === page ? 'bg-[#D67C2C] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => setPage(Math.min(page + 1, totalPages))}
                                        disabled={page === totalPages}
                                        className={`p-2 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Error Modal - similar to LessonPlanHistory.js DeleteConfirmationModal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-5 h-5" />
                                <span>Error Loading Parents</span>
                            </div>
                        }
                        open={errorModalVisible}
                        onCancel={() => setErrorModalVisible(false)}
                        footer={[
                            <button
                                key="retry"
                                onClick={handleRetryFetch}
                                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors mr-2"
                            >
                                Retry
                            </button>,
                            <button
                                key="cancel"
                                onClick={() => setErrorModalVisible(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>,
                        ]}
                    >
                        <div className="py-4">
                            <p className="mb-4">Failed to fetch parents. This could be due to:</p>
                            <ul className="list-disc pl-5 space-y-2">
                                <li>Network connection issue</li>
                                <li>Authentication token not fully loaded</li>
                                <li>Server temporarily unavailable</li>
                            </ul>
                            <p className="mt-4">Please try refreshing the page or click "Retry" to attempt loading
                                again.</p>
                        </div>
                    </Modal>

                    {/* Add Parent Modal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2">
                                <Plus className="w-5 h-5 text-[#D67C2C]" />
                                <span>Add New Parent</span>
                            </div>
                        }
                        open={addModalOpen}
                        onCancel={() => setAddModalOpen(false)}
                        footer={null}
                    >
                        <form
                            className="py-4"
                            onSubmit={handleCreateParent}
                        >
                            <div className="space-y-5">
                                <div>
                                    <label className="font-medium block mb-2 text-gray-700">Full Name</label>
                                    <input
                                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                                        required
                                        placeholder="Enter parent's full name"
                                        value={formParent.name}
                                        onChange={(e) => setFormParent((d) => ({ ...d, name: e.target.value }))}
                                    />
                                </div>

                                <div>
                                    <label className="font-medium block mb-2 text-gray-700">Email Address</label>
                                    <input
                                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D67C2C] transition-colors"
                                        required
                                        type="email"
                                        placeholder="parent@example.com"
                                        value={formParent.email}
                                        onChange={(e) => setFormParent((d) => ({ ...d, email: e.target.value }))}
                                    />
                                </div>

                                <div>
                                    <label className="font-medium block mb-2 text-gray-700">Password</label>
                                    <div className="relative">
                                        <input
                                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-[#D67C2C] transition-colors"
                                            required
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Create a strong password"
                                            value={formParent.password}
                                            onChange={(e) => setFormParent((d) => ({ ...d, password: e.target.value }))}
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600"
                                            onClick={() => setShowPassword((v) => !v)}
                                        >
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Share this password with the parent securely. No onboarding email is sent.
                                    </p>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setAddModalOpen(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Plus className="w-5 h-5" />
                                        Add Parent
                                    </button>
                                </div>
                            </div>
                        </form>
                    </Modal>

                    {/* CSV Upload Modal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2">
                                <Upload className="w-5 h-5 text-[#D67C2C]" />
                                <span>Bulk Upload Parents</span>
                            </div>
                        }
                        open={csvModalOpen}
                        onCancel={() => setCSVModalOpen(false)}
                        footer={null}
                    >
                        <form
                            className="py-4"
                            onSubmit={handleBulkUpload}
                        >
                            <div className="bg-orange-50 p-4 rounded-lg mb-6">
                                <div className="text-[#D67C2C] font-medium mb-2">Required CSV Format:</div>
                                <div className="font-mono text-sm bg-white p-2 rounded border border-orange-200">
                                    name,email,password
                                </div>
                                <div className="text-sm text-[#D67C2C] mt-2">
                                    Enter a password for every new parent and share it securely. No onboarding email is sent.
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="font-medium block mb-2 text-gray-700">Upload CSV File</label>
                                <div className="flex flex-col gap-4">
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleCSVChange}
                                        className="border-2 border-dashed border-gray-200 p-4 rounded-lg text-gray-700 focus:outline-none focus:border-[#D67C2C] transition-colors cursor-pointer"
                                    />
                                    <button
                                        type="button"
                                        onClick={downloadCSVTemplate}
                                        className="inline-flex gap-2 text-[#D67C2C] hover:text-[#c06a25] transition-colors items-center text-sm font-medium"
                                    >
                                        <FileDown className="w-4 h-4" /> Download Template CSV
                                    </button>
                                </div>
                            </div>

                            {/* Validation Summary */}
                            {csvValidation && (
                                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                                    <div className="font-medium text-gray-700 mb-3">Validation Summary</div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                            <span>Valid: <strong>{csvValidation.valid_count || 0}</strong></span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <span>Invalid: <strong>{csvValidation.invalid_emails?.length || 0}</strong></span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                            <span>Duplicates: <strong>{csvValidation.duplicate_emails_in_csv?.length || 0}</strong></span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                                            <span>Existing: <strong>{csvValidation.existing_emails_in_db?.length || 0}</strong></span>
                                        </div>
                                    </div>
                                    {csvValidation.summary && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <span className="text-green-600 font-medium">
                                                ✓ {csvValidation.summary.can_upload} will be uploaded
                                            </span>
                                            {csvValidation.summary.will_skip > 0 && (
                                                <span className="text-orange-600 ml-4">
                                                    ⚠ {csvValidation.summary.will_skip} will be skipped
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {validating && (
                                <div className="mb-4 flex items-center gap-2 text-gray-600">
                                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-[#D67C2C] border-t-transparent"></div>
                                    <span>Validating CSV...</span>
                                </div>
                            )}

                            {csvPreview.length > 0 && (
                                <div className="mb-6">
                                    <div className="font-medium mb-2 text-gray-700">Preview (First 20 rows):</div>
                                    <div className="bg-gray-50 rounded-lg p-4 text-sm overflow-x-auto max-h-60 border border-gray-200">
                                        <div className="font-mono font-semibold text-gray-700 py-2 border-b-2 border-gray-300 flex gap-4">
                                            <span className="w-10">Row</span>
                                            <span className="flex-1">Name</span>
                                            <span className="flex-1">Email</span>
                                            <span className="w-32">Status</span>
                                        </div>
                                        {csvPreview.map((row, i) => {
                                            const statusColors = {
                                                valid: "bg-green-50 border-l-4 border-green-500",
                                                invalid_email: "bg-red-50 border-l-4 border-red-500",
                                                duplicate: "bg-yellow-50 border-l-4 border-yellow-500",
                                                exists: "bg-orange-50 border-l-4 border-orange-500"
                                            };
                                            const statusLabels = {
                                                valid: <span className="text-green-600">✓ Valid</span>,
                                                invalid_email: <span className="text-red-600">✗ {row.reason}</span>,
                                                duplicate: <span className="text-yellow-600">⚠ Duplicate</span>,
                                                exists: <span className="text-orange-600">⚠ Exists</span>
                                            };

                                            return (
                                                <div
                                                    key={i}
                                                    className={`font-mono py-2 border-b last:border-0 flex gap-4 items-center ${statusColors[row.status] || ""}`}
                                                >
                                                    <span className="w-10 text-gray-500">{row.row}</span>
                                                    <span className="flex-1 truncate">{row.name || "-"}</span>
                                                    <span className="flex-1 truncate">{row.email || "-"}</span>
                                                    <span className="w-32 text-xs">
                                                        {statusLabels[row.status] || row.status}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}


                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCSVModalOpen(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-[#D67C2C] text-white rounded-lg py-3 font-semibold hover:bg-[#c06a25] transition-colors flex items-center justify-center gap-2"
                                    disabled={!csvFile}
                                >
                                    <Upload className="w-5 h-5" />
                                    Upload Parents
                                </button>
                            </div>
                        </form>
                    </Modal>

                    {/* Link Children Modal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2">
                                <LinkIcon className="w-5 h-5 text-[#D67C2C]" />
                                <span>Link Children to Parent</span>
                            </div>
                        }
                        open={linkChildrenModalOpen}
                        onCancel={() => setLinkChildrenModalOpen(false)}
                        footer={null}
                    >
                        <form
                            className="py-4"
                            onSubmit={handleLinkChildren}
                        >
                            <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                                <div className="flex items-center gap-4">
                                    <div
                                        className="bg-white rounded-full w-12 h-12 flex items-center justify-center text-[#D67C2C] shadow-md">
                                        <User2 className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-800">{selectedParent?.name}</div>
                                        <div className="text-sm text-gray-600">{selectedParent?.email}</div>
                                    </div>
                                </div>
                            </div>

                            {loadingStudents ? (
                                <div className="flex items-center justify-center gap-3 py-8">
                                    <div
                                        className="w-8 h-8 text-[#D67C2C] animate-spin rounded-full border-4 border-orange-500 border-t-transparent"></div>
                                    <span className="text-gray-600">Loading available students...</span>
                                </div>
                            ) : availableStudents.length ? (
                                <div className="mb-6">
                                    <label className="font-medium block mb-3 text-gray-700">
                                        Select Students to Link as Children
                                    </label>
                                    <div className="border-2 border-gray-200 rounded-lg p-4 max-h-80 overflow-y-auto">
                                        <div className="space-y-2">
                                            {availableStudents.map((student) => (
                                                <label key={student.id}
                                                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors">
                                                    <input
                                                        type="checkbox"
                                                        value={student.id}
                                                        checked={selectedStudents.includes(student.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedStudents(prev => [...prev, student.id]);
                                                            } else {
                                                                setSelectedStudents(prev => prev.filter(id => id !== student.id));
                                                            }
                                                        }}
                                                        className="rounded border-gray-300 text-[#D67C2C] focus:ring-[#D67C2C]"
                                                    />
                                                    <div
                                                        className="bg-[#D67C2C] bg-opacity-10 rounded-full w-10 h-10 flex items-center justify-center">
                                                        {student.profile_picture_url ? (
                                                            <img
                                                                src={server_url + student.profile_picture_url}
                                                                alt={student.name}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <Baby className="w-5 h-5 text-[#D67C2C]" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{student.name}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {student.email} •
                                                            Grade {student.grade || student.class_grade || 'N/A'}
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6 bg-gray-50 rounded-lg mb-6">
                                    <div className="text-gray-500 mb-2">No students available to link.</div>
                                    <div className="text-sm text-gray-400">All students may already be linked or none
                                        exist
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setLinkChildrenModalOpen(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={classNames(
                                        "flex-1 rounded-lg py-3 font-semibold transition-colors flex items-center justify-center gap-2",
                                        availableStudents.length && selectedStudents.length
                                            ? "bg-[#D67C2C] text-white hover:bg-[#c06a25]"
                                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    )}
                                    disabled={!availableStudents.length || !selectedStudents.length}
                                >
                                    <LinkIcon className="w-5 h-5" />
                                    Link {selectedStudents.length || 'No'} Selected
                                    {selectedStudents.length === 1 ? ' Student' : ' Students'}
                                </button>
                            </div>
                        </form>
                    </Modal>

                    {/* Delete Confirmation Modal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-5 h-5" />
                                <span>Confirm Deletion - Step {deleteConfirmModal.step} of 3</span>
                            </div>
                        }
                        open={deleteConfirmModal.open}
                        onCancel={() => setDeleteConfirmModal({
                            open: false,
                            type: 'single',
                            email: '',
                            parents: [],
                            step: 1
                        })}
                        footer={null}
                    >
                        <div className="py-4">
                            {deleteConfirmModal.step === 1 && (
                                <div className="text-center">
                                    <div
                                        className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <AlertTriangle className="w-8 h-8 text-red-500" />
                                    </div>
                                    <p className="text-gray-700 mb-4">
                                        {deleteConfirmModal.type === 'single'
                                            ? `Are you sure you want to delete this parent?`
                                            : `Are you sure you want to delete ${deleteConfirmModal.parents.length} parents?`
                                        }
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        This action cannot be undone. Click "Continue" to proceed.
                                    </p>
                                </div>
                            )}

                            {deleteConfirmModal.step === 2 && (
                                <div className="text-center">
                                    <div
                                        className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <AlertTriangle className="w-8 h-8 text-yellow-500" />
                                    </div>
                                    <p className="text-gray-700 mb-4 font-semibold">
                                        Final Warning!
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {deleteConfirmModal.type === 'single'
                                            ? 'The parent and all associated data will be permanently deleted.'
                                            : `All ${deleteConfirmModal.parents.length} parents and their associated data will be permanently deleted.`
                                        }
                                    </p>
                                </div>
                            )}

                            {deleteConfirmModal.step === 3 && (
                                <div className="text-center">
                                    <div
                                        className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Trash2 className="w-8 h-8 text-red-500" />
                                    </div>
                                    <p className="text-gray-700 mb-4 font-bold text-red-600">
                                        Last Chance!
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Click "Delete Forever" to permanently remove the data.
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setDeleteConfirmModal({
                                        open: false,
                                        type: 'single',
                                        email: '',
                                        parents: [],
                                        step: 1
                                    })}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirmation}
                                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${deleteConfirmModal.step === 3
                                        ? 'bg-red-500 hover:bg-red-600 text-white'
                                        : 'bg-[#D67C2C] hover:bg-[#c06a25] text-white'
                                        }`}
                                >
                                    {deleteConfirmModal.step === 3 ? 'Delete Forever' : 'Continue'}
                                </button>
                            </div>

                            <div className="mt-4 text-center">
                                <div className="flex justify-center gap-2">
                                    {[1, 2, 3].map(step => (
                                        <div
                                            key={step}
                                            className={`w-3 h-3 rounded-full ${step <= deleteConfirmModal.step
                                                ? 'bg-red-500'
                                                : 'bg-gray-200'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Modal>

                    {/* Parent Detail Modal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2">
                                <User2 className="w-5 h-5 text-[#D67C2C]" />
                                <span>Parent Details</span>
                            </div>
                        }
                        open={detailModalOpen}
                        onCancel={() => setDetailModalOpen(false)}
                        footer={[
                            <button
                                key="close"
                                onClick={() => setDetailModalOpen(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>
                        ]}
                    >
                        <div className="flex items-center gap-5 mb-8 p-6 bg-orange-50 rounded-lg">
                            <div
                                className="bg-white rounded-full w-24 h-24 flex items-center justify-center text-[#D67C2C] shadow-md">
                                {selectedParent?.profile_picture_url ? (
                                    <img
                                        src={server_url + selectedParent.profile_picture_url}
                                        alt={selectedParent.name}
                                        className="w-24 h-24 rounded-full object-cover"
                                    />
                                ) : (
                                    <User2 className="w-12 h-12" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">{selectedParent?.name || "--"}</h3>
                                <div className="text-gray-600 flex items-center gap-2 mt-1">
                                    <Mail className="w-4 h-4" /> {selectedParent?.email || "--"}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                <div className="text-sm text-gray-500">Account ID</div>
                                <div className="font-medium text-gray-700">{selectedParent?.id || "--"}</div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                <div className="text-sm text-gray-500">Role</div>
                                <div
                                    className="font-medium text-gray-700 capitalize">{selectedParent?.role || "--"}</div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                <div className="text-sm text-gray-500">Created At</div>
                                <div className="font-medium text-gray-700">
                                    {formatDate(selectedParent?.created_at)}
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                                <div className="text-sm text-gray-500">Last Login</div>
                                <div className="font-medium text-gray-700">
                                    {selectedParent?.last_login
                                        ? formatDate(selectedParent.last_login)
                                        : "Never logged in"}
                                </div>
                            </div>
                        </div>

                        {/* Associated Students */}
                        {selectedParent?.children && selectedParent.children.length > 0 ? (
                            <div className="mb-6">
                                <h4 className="text-lg font-semibold mb-3 text-gray-800">Associated Students</h4>
                                <div className="space-y-2">
                                    {selectedParent.children.map((child, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <div
                                                className="bg-[#D67C2C] bg-opacity-10 rounded-full w-10 h-10 flex items-center justify-center text-[#D67C2C]">
                                                <Baby className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <div className="font-medium">{child.name}</div>
                                                <div className="text-sm text-gray-500">Grade {child.grade}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                                No students associated with this parent.
                            </div>
                        )}

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setDetailModalOpen(false);
                                    openLinkChildren(selectedParent);
                                }}
                                className="px-5 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors flex items-center gap-2"
                            >
                                <LinkIcon className="w-4 h-4" />
                                Link Children
                            </button>
                            <button
                                onClick={() => {
                                    setDetailModalOpen(false);
                                    openDeleteConfirmation('single', selectedParent.email);
                                }}
                                className="px-5 py-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                            </button>
                        </div>
                    </Modal>

                    {/* Skipped Records Modal */}
                    <Modal
                        title={
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-orange-500" />
                                <span>Skipped Records</span>
                            </div>
                        }
                        open={showSkippedModal}
                        onCancel={() => setShowSkippedModal(false)}
                        footer={
                            <button
                                onClick={() => setShowSkippedModal(false)}
                                className="px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                            >
                                Close
                            </button>
                        }
                        width={600}
                    >
                        {uploadResult?.skipped && uploadResult.skipped.length > 0 ? (
                            <div>
                                <p className="text-gray-600 mb-4">
                                    The following {uploadResult.skipped.length} record(s) were skipped during upload:
                                </p>
                                <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                                                <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
                                                <th className="px-4 py-2 text-left font-medium text-gray-700">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uploadResult.skipped.map((item, idx) => (
                                                <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                                    <td className="px-4 py-2 text-gray-600">{item.email}</td>
                                                    <td className="px-4 py-2 text-gray-600">{item.name || "-"}</td>
                                                    <td className="px-4 py-2">
                                                        <span className="text-red-600 text-xs bg-red-50 px-2 py-1 rounded">
                                                            {item.reason}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                                    <strong>Tip:</strong> Fix these issues in your CSV file and re-upload the corrected records.
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-500">No skipped records to display.</p>
                        )}
                    </Modal>
                </div>
            </div>
        </>
    );
}

// Modal Overlay for antd modals
export default ParentView;
